// src/features/ownerAdmin/CreatorGate.tsx
// Wraps every creator-only route (Bot Health, and the split-up Owner
// Admin pages) with the same two-factor check: Discord ID (isCreator)
// and the passcode unlock. Extracted from what used to be inline
// inside OwnerAdminPage, now that Bot Health needs the exact same
// gating and Owner Admin itself is being split into several pages
// that would otherwise each need to duplicate this.
import { useState, type FormEvent } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { useUnlockStatus, useUnlock } from './useCreatorData';

function PasscodeGate({ onUnlocked }: { onUnlocked: () => void }) {
  const [passcode, setPasscode] = useState('');
  const unlock = useUnlock();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!passcode) return;
    unlock.mutate(passcode, { onSuccess: onUnlocked });
  }

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Second factor required</p>
        <h1 className="page-header__title">Owner Admin</h1>
      </div>
      <article className="card" style={{ maxWidth: 380 }}>
        <div className="card__content">
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 1rem' }}>
            Enter the owner passcode to continue. This is separate from your Discord login — a
            second factor specifically for this section.
          </p>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <input
              className="input"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              autoFocus
            />
            <button className="button-primary" type="submit" disabled={!passcode || unlock.isPending}>
              {unlock.isPending ? 'Checking…' : 'Unlock'}
            </button>
            {unlock.isError && (
              <span style={{ color: 'var(--negative)', fontSize: 13 }}>
                Incorrect passcode, or too many attempts — wait a few minutes and try again.
              </span>
            )}
          </form>
        </div>
      </article>
    </>
  );
}

export function CreatorGate() {
  const { user } = useAuth();
  const { data: unlockStatus, isLoading: unlockLoading, refetch: refetchUnlockStatus } = useUnlockStatus();

  // Client-side guard is UX only — the real gate is server-side
  // (requireCreator on every /api/creator/* route). This just avoids
  // showing a page full of failed-request errors to someone who
  // navigated here directly by URL without creator access.
  if (!user?.isCreator) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-header__title">Owner Admin</h1>
        </div>
        <article className="card">
          <div className="card__content">
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>This section is only available to the bot owner.</p>
          </div>
        </article>
      </>
    );
  }

  // Same UX-only principle as above — the real gate is requireUnlocked
  // on every /api/creator/* data route.
  if (unlockLoading) return null;
  if (!unlockStatus?.unlocked) {
    return <PasscodeGate onUnlocked={() => refetchUnlockStatus()} />;
  }

  return <Outlet />;
}
